import React, { useState, useEffect } from 'react';
import { listActiveModules, syncModuleCatalog, InstalledModuleRecord, toggleModule, isModuleEnabled } from '../modules/ModuleRegistry.js';
import { SignatureBadge } from './SignatureBadge.js';
import { RefreshIcon } from '../icons.js';
import { ModuleSettingsModal } from '../modules/ModuleSettingsModal.js';
import { Settings } from 'lucide-react';

export const ExtensionManagerPane: React.FC = () => {
  const [modules, setModules] = useState<InstalledModuleRecord[]>([]);
  const [manifests, setManifests] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);

  // We need local state to trigger re-renders when toggling modules
  // because isModuleEnabled is imperative
  const [enabledState, setEnabledState] = useState<Record<string, boolean>>({});
  const [settingsModalMod, setSettingsModalMod] = useState<InstalledModuleRecord | null>(null);

  const loadModules = async () => {
    setLoading(true);
    try {
      const active = await syncModuleCatalog();
      setModules(active);
      
      const enabledMap: Record<string, boolean> = {};
      const manifestMap: Record<string, any> = {};

      for (const m of active) {
        enabledMap[m.id] = isModuleEnabled(m.id);
        
        try {
          const manifestUrl = m.bundleUrl.replace(/dist\/bundle\.js$/, 'manifest.signed.json');
          const res = await fetch(manifestUrl);
          if (res.ok) {
            manifestMap[m.id] = await res.json();
          }
        } catch (err) {
          console.warn(`Could not load manifest for ${m.id}`, err);
        }
      }
      
      setEnabledState(enabledMap);
      setManifests(manifestMap);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  const handleToggle = (moduleId: string) => {
    const isCurrentlyEnabled = enabledState[moduleId];
    toggleModule(moduleId, !isCurrentlyEnabled);
    setEnabledState(prev => ({ ...prev, [moduleId]: !isCurrentlyEnabled }));
  };

  return (
    <div data-testid="drawer-extension-manager" style={{ padding: '24px', color: '#f8fafc', height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>Extension Manager</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
            Manage installed sovereign modules, verify cryptographic signatures, and control capabilities.
          </p>
        </div>
        <button
          onClick={loadModules}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            color: '#f8fafc',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {loading ? 'Syncing...' : <><RefreshIcon size={14} /> Refresh Catalog</>}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {modules.map((m) => {
          const manifest = manifests[m.id];
          const isEnabled = enabledState[m.id];

          return (
            <div
              key={m.id}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>{m.name}</h3>
                    <span style={{ fontSize: '11px', color: '#94a3b8', background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                      v{m.version}
                    </span>
                    <SignatureBadge moduleId={m.id} manifest={manifest} />
                  </div>
                  <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#cbd5e1' }}>
                    {manifest?.description || 'No description available.'}
                  </p>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {manifest?.settingsSchema && Object.keys(manifest.settingsSchema).length > 0 && (
                    <button
                      onClick={() => setSettingsModalMod(m)}
                      title="Module Settings"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        background: 'rgba(56, 189, 248, 0.1)',
                        border: '1px solid rgba(56, 189, 248, 0.2)',
                        color: '#38bdf8',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                    >
                      <Settings size={16} />
                    </button>
                  )}
                  <button
                    data-testid={`module-toggle-${m.id}`}
                    onClick={() => handleToggle(m.id)}
                    style={{
                      padding: '8px 16px',
                      background: isEnabled ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      border: `1px solid ${isEnabled ? '#ef4444' : '#10b981'}`,
                      color: isEnabled ? '#fca5a5' : '#34d399',
                      borderRadius: '8px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {isEnabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>

              {manifest?.permissions && manifest.permissions.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Capabilities:</span>
                  {manifest.permissions.map((p: string) => (
                    <span
                      key={p}
                      style={{
                        fontSize: '10px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        color: '#a5b4fc',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontFamily: 'monospace',
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {modules.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
            No modules found in the registry.
          </div>
        )}
      </div>

      {settingsModalMod && (
        <ModuleSettingsModal
          isOpen={true}
          moduleRecord={{
            ...settingsModalMod,
            settingsSchema: manifests[settingsModalMod.id]?.settingsSchema,
          }}
          onClose={() => setSettingsModalMod(null)}
        />
      )}
    </div>
  );
};
